import moment from 'moment';
import {
  MY_TASKS_GROUP_FILTER_TYPE,
  PrioritySortOrder,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  STATUS_ORDER,
  STATUS_TYPE,
  TASK_PRIORITY,
  TASK_PRIORITY_LABELS
} from '../global/constants';
import { updateTaskFilterGrouping, updateTaskFilterOrder } from '../helpers/common';
import {
  MobxFilteredCompletedTasksListInterface,
  MobxFilteredTasksListInterface,
  MobxMyTaskModelInterface
} from '../interfaces/TaskInterface';
import { ProjectModel } from '../mobx/models/project';
import { TaskModel } from '../mobx/models/task';
import { rootStore } from '../mobx/rootStore';
import { startOfWeek } from 'date-fns';

// /** ---------------------- PROJECT - TASK PAGE FILTER SERVICES ------------------------------ */

export const getTasksListByUser = (tasksList: TaskModel[], projectDetail: ProjectModel) => {
  const projectUsers = new Set(projectDetail?.users?.map((item) => item?.id));

  const { tasksGroupedByUser, users, tasksWithNoUsersTasks, tasksWithNoUsersCompletedTasks } = tasksList?.reduce(
    (acc, task) => {
      if (!task?.users?.length) {
        (task?.status ? acc.tasksWithNoUsersCompletedTasks : acc.tasksWithNoUsersTasks).push(task);
      } else {
        task.users.forEach((user) => {
          if (projectUsers.has(user?.id)) {
            acc.users[user.id] = user.name;
            (acc.tasksGroupedByUser[user.id] ||= []).push(task);
          }
        });
      }
      return acc;
    },
    {
      tasksGroupedByUser: {} as Record<string, TaskModel[]>,
      users: {} as Record<string, string>,
      tasksWithNoUsersTasks: [] as TaskModel[],
      tasksWithNoUsersCompletedTasks: [] as TaskModel[]
    }
  );

  const otherTasks: MobxFilteredCompletedTasksListInterface = {
    tasks: tasksWithNoUsersTasks,
    completedTasks: tasksWithNoUsersCompletedTasks
  };
  const filteredTasks: MobxFilteredTasksListInterface[] = Object.entries(tasksGroupedByUser).map(
    ([userId, userTasks]) => ({
      id: userId,
      name: users[userId],
      tasks: userTasks.filter((task) => !task?.status),
      completedTasks: userTasks.filter((task) => task?.status)
    })
  );

  return { filteredTasks: filteredTasks, otherTasks: otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTasksListByStatus = (
  tasksList: TaskModel[],
  projectDetails: ProjectModel,
  isShowRecentlyCompletedTask: boolean
) => {
  let otherTasks: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };

  const groupedTasks = new Map<string, TaskModel[]>();
  tasksList?.forEach((task) => {
    const statusId = task.statusId || 'other';
    if (!groupedTasks.has(statusId)) {
      groupedTasks.set(statusId, []);
    }
    groupedTasks.get(statusId)!.push(task);
  });

  const defaultTasks = groupedTasks.get('other') || [];

  const filteredTasks: MobxFilteredTasksListInterface[] = (projectDetails?.statusData || [])
    .map(({ _id: id, Name, Type }) => {
      const statusTasks = groupedTasks.get(id) || [];
      const additionalTasks = projectDetails?.defaultStatus
        ? projectDetails?.defaultStatus['_id'] === id
          ? defaultTasks
          : []
        : [];
      // Merge the tasks
      let allTasks = statusTasks.concat(additionalTasks);

      // Sort tasks if the status is COMPLETED (5)
      if (Type === STATUS_TYPE.COMPLETED && isShowRecentlyCompletedTask) {
        allTasks.sort((a, b) => new Date(b.updatedTime!).getTime() - new Date(a.updatedTime!).getTime());
      }

      const result: MobxFilteredTasksListInterface = {
        id: id,
        name: Name || '',
        tasks: allTasks,
        status: Type,
        completedTasks: []
      };

      return result;
    })
    .sort((a, b) => STATUS_ORDER.indexOf(a.status!) - STATUS_ORDER.indexOf(b.status!));

  return { filteredTasks, otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTasksByPriority = (tasksList: TaskModel[]) => {
  const otherTasks: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };
  const filteredTasksMap = tasksList.reduce((map, task) => {
    const priority = task.priority || TASK_PRIORITY.NO_PRIORITY;

    if (!map.has(priority)) {
      map.set(priority, {
        id: priority,
        name: TASK_PRIORITY_LABELS[priority],
        tasks: [],
        completedTasks: []
      });
    }

    const current = map.get(priority);
    task.status ? current?.completedTasks?.push(task) : current?.tasks?.push(task);

    return map;
  }, new Map<number, MobxFilteredTasksListInterface>());

  const filteredTasks: MobxFilteredTasksListInterface[] = Array.from(filteredTasksMap.values());
  filteredTasks.sort((a, b) => PrioritySortOrder.indexOf(a.id as number) - PrioritySortOrder.indexOf(b.id as number));
  return { filteredTasks, otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTasksListByNoGrouping = (tasksList: TaskModel[], projectId: string) => {
  const otherTasks: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };

  const { tasks, completedTasks } = tasksList.reduce(
    (acc, task) => {
      if (task.status) {
        acc.completedTasks.push(task);
      } else {
        acc.tasks.push(task);
      }
      return acc;
    },
    { tasks: [] as TaskModel[], completedTasks: [] as TaskModel[] }
  );

  const filteredTasks: MobxFilteredTasksListInterface[] = [
    {
      id: 'No Grouping',
      index: 0,
      name: 'Other Tasks',
      projectId,
      tasks,
      completedTasks
    }
  ];

  return { filteredTasks: filteredTasks, otherTasks: otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTaskListByGroup = (taskList: TaskModel[]) => {
  const taskGroups = rootStore.projectStore.currentProject?.taskGroups || [];
  const taskGroupIds = new Set(taskGroups.map((group) => group.id));
  const groupedTasks = taskList.reduce<Record<string, TaskModel[]>>((acc, item) => {
    // If item has groupId but that group doesn't exist in taskGroups, treat as 'other'
    const groupId = item.groupId && taskGroupIds.has(item.groupId) ? item.groupId : 'other';
    const key = item.status ? `${groupId}_completed` : groupId;

    if (!acc[key]) acc[key] = [];
    acc[key].push(item);

    return acc;
  }, {});

  const taskWithGroups: MobxFilteredTasksListInterface[] = taskGroups?.map((item) => ({
    ...item,
    tasks: groupedTasks[item?.id] || [],
    completedTasks: groupedTasks[`${item?.id}_completed`] || [],
    id: item?.id
  }));

  const otherTask: MobxFilteredCompletedTasksListInterface = {
    tasks: groupedTasks['other'] || [],
    completedTasks: groupedTasks['other_completed'] || []
  };
  return { filteredTasks: taskWithGroups, otherTasks: otherTask };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTaskListByMilestone = (taskList: TaskModel[]) => {
  const milestoneList = rootStore.projectStore.currentProject?.milestones || [];
  // Create a milestone map for quick lookup
  const milestoneMap = milestoneList.reduce((acc: Record<string, string>, milestone: any) => {
    acc[milestone.id] = milestone.milestoneName;
    return acc;
  }, {});

  // Initialize the milestoneWiseTask array and otherTasks array
  const milestoneWiseTask: MobxFilteredTasksListInterface[] = [];
  const otherTasks: TaskModel[] = [];

  // Group tasks by milestone using reduce
  const tasksGroupedByMilestone = taskList.reduce((acc: Record<string, TaskModel[]>, task) => {
    const milestoneId = task.milestoneId || 'Other';
    if (!acc[milestoneId]) {
      acc[milestoneId] = [];
    }
    acc[milestoneId].push(task);
    return acc;
  }, {});

  // Process grouped tasks
  Object.entries(tasksGroupedByMilestone).forEach(([milestoneId, tasks]) => {
    const milestoneName = milestoneMap[milestoneId] || 'Other';

    if (milestoneName === 'Other') {
      otherTasks.push(...tasks);
    } else {
      milestoneWiseTask.push({
        id: milestoneId,
        tasks: tasks.filter((task) => !task.status),
        completedTasks: tasks.filter((task) => task.status),
        name: milestoneName
      });
    }
  });

  // Add other tasks if any
  if (otherTasks.length > 0) {
    milestoneWiseTask.push({
      id: 'other',
      tasks: otherTasks.filter((task) => !task.status),
      completedTasks: otherTasks.filter((task) => task.status),
      name: 'Other'
    });
  }

  // Convert to array
  const tasksRef: MobxFilteredTasksListInterface[] = Object.values(milestoneWiseTask);
  const otherTask: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };

  return { filteredTasks: tasksRef, otherTasks: otherTask };
};

//----------------------------------------------------------------------------------------------------------------------

export const sortByDueDate = (tasks: TaskModel[]) => {
  return tasks.toSorted((a, b) => {
    const dateA = a.dueDate ? new Date(a.dueDate).getTime() : new Date().getTime();
    const dateB = b.dueDate ? new Date(b.dueDate).getTime() : new Date().getTime();
    return dateA - dateB;
  });
};

export const sortByCreatedDate = (tasks: TaskModel[]) => {
  return tasks.toSorted((a, b) => {
    const dateA = a.createdOn ? new Date(a.createdOn).getTime() : new Date().getTime();
    const dateB = b.createdOn ? new Date(b.createdOn).getTime() : new Date().getTime();
    return dateA - dateB;
  });
};

export const sortByPriority = (tasks: TaskModel[]) => {
  return tasks.sort(
    (a, b) =>
      PrioritySortOrder.indexOf(a.priority || TASK_PRIORITY.NO_PRIORITY) -
      PrioritySortOrder.indexOf(b.priority || TASK_PRIORITY.NO_PRIORITY)
  );
};

export const sortByIndex = (tasks: TaskModel[]) => {
  return tasks.toSorted((a, b) => a.index - b.index);
};

export const taskSorter = (
  type: number,
  tasks: TaskModel[],
  completedTask: TaskModel[],
  isShowRecentlyCompletedTask?: boolean
) => {
  let sortedTasks = tasks;
  let sortedCompletedTasks = completedTask;
  if (isShowRecentlyCompletedTask) {
    sortedTasks = sortedTasks.sort((a, b) => new Date(b.updatedTime!).getTime() - new Date(a.updatedTime!).getTime());
    sortedCompletedTasks = sortedCompletedTasks.sort(
      (a, b) => new Date(b.updatedTime!).getTime() - new Date(a.updatedTime!).getTime()
    );
    return { tasks: sortedTasks, completedTasks: sortedCompletedTasks };
  }
  switch (type) {
    case SHOW_TASK_BY_ORDER.DUE_DATE:
      sortedTasks = sortByDueDate(tasks);
      sortedCompletedTasks = sortByDueDate(completedTask);
      break;
    case SHOW_TASK_BY_ORDER.PRIORITY:
      sortedTasks = sortByPriority(tasks);
      sortedCompletedTasks = sortByPriority(completedTask);
      break;
    case SHOW_TASK_BY_ORDER.CUSTOM:
      sortedTasks = sortByIndex(tasks);
      sortedCompletedTasks = sortByIndex(completedTask);
      break;
    default:
      sortedTasks = sortByCreatedDate(tasks);
      sortedCompletedTasks = sortByCreatedDate(completedTask);
      break;
  }
  return { tasks: sortedTasks, completedTasks: sortedCompletedTasks };
};

export const applyTaskFilter = (tasks: TaskModel[], project: ProjectModel) => {
  const { showRecentlyCompletedTask } = project?.taskFilterType;
  const updatedGroupingStatus = updateTaskFilterGrouping(project);
  const updatedOrderByStatus = updateTaskFilterOrder(project);
  let result: { filteredTasks: MobxFilteredTasksListInterface[]; otherTasks: MobxFilteredCompletedTasksListInterface } =
    {
      filteredTasks: [],
      otherTasks: { tasks: [], completedTasks: [] }
    };
  // Apply grouping based on filter
  switch (updatedGroupingStatus) {
    case SHOW_TASK_GROUPING_TYPE.NO_GROUPING:
      result = getTasksListByNoGrouping(tasks, project.id);
      break;
    case SHOW_TASK_GROUPING_TYPE.DEFAULT:
      if (project.statusEnable) {
        result = getTasksListByStatus(tasks, project, showRecentlyCompletedTask);
      } else if (project.isGroupEnabled) {
        result = getTaskListByGroup(tasks);
      } else {
        result = getTasksListByNoGrouping(tasks, project.id);
      }
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY:
      result = getTasksByPriority(tasks);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER:
      result = getTasksListByUser(tasks, project);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE:
      result = getTaskListByMilestone(tasks);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS:
      result = getTasksListByStatus(tasks, project, showRecentlyCompletedTask);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP:
      result = getTaskListByGroup(tasks);
      break;
    default:
      result = getTaskListByGroup(tasks);
      break;
  }

  // Apply sorting to each group
  if (result.filteredTasks && result.filteredTasks.length > 0) {
    result.filteredTasks = result.filteredTasks.map((groupItem) => {
      if (groupItem.tasks && groupItem.completedTasks) {
        let sorted;
        if (
          updatedGroupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS &&
          groupItem.status === STATUS_TYPE.COMPLETED &&
          showRecentlyCompletedTask
        ) {
          sorted = taskSorter(updatedOrderByStatus, groupItem.tasks, groupItem.completedTasks, true);
        } else {
          sorted = taskSorter(updatedOrderByStatus, groupItem.tasks, groupItem.completedTasks, false);
        }
        return {
          ...groupItem,
          tasks: sorted.tasks,
          completedTasks: sorted.completedTasks
        };
      }
      return groupItem;
    });
  }

  // Sort other tasks
  if (result.otherTasks) {
    const sorted = taskSorter(
      updatedOrderByStatus,
      result.otherTasks.tasks,
      result.otherTasks.completedTasks || [],
      false
    );
    result.otherTasks = { tasks: sorted.tasks, completedTasks: sorted.completedTasks };
  }
  return result;
};

export const filterCompletedTasksByDate = (project: ProjectModel): TaskModel[] => {
  const { taskFilterType: taskFilter, tasks } = project;
  const groupingStatus = updateTaskFilterGrouping(project);
  if (groupingStatus !== SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS) {
    return tasks.filter((task) => !task.parentTaskId);
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
  let endDate: Date | null = null;

  const cancelledStatus = project.statusData?.find((item) => item.Type === STATUS_TYPE.CANCELLED);

  switch (taskFilter.showCompletedTaskByFilter) {
    case SHOW_COMPLETED_TASK_BY_DATE.YESTERDAY:
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.LAST_WEEK:
      endDate = new Date(today);
      endDate.setDate(today.getDate() - today.getDay() - 7);
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH:
      endDate = new Date(today.getFullYear(), today.getMonth() - 1, new Date().getDate());
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.NONE:
      return tasks.filter((task) => !task.parentTaskId && (!task.status || task.statusId === cancelledStatus?.['_id']));

    case SHOW_COMPLETED_TASK_BY_DATE.ALL:
    default:
      return tasks.filter((task) => !task.parentTaskId);
  }
  return tasks.filter((task) => {
    return (
      !task.parentTaskId &&
      (!task.status || task.statusId === cancelledStatus?.['_id'] || (endDate && task.updatedTime >= endDate))
    );
  });
};

//---------------------------------------- MY Task filter services -----------------------------------------------------

/** Group tasks by group name */
const groupTasksByGroupName = (tasks: TaskModel[]) => {
  const groupedTasks = tasks.map((task) => {
    const title = task?.project?.taskGroups?.find((group) => group?.id === task?.groupId)?.name || 'Other';
    return { task, title };
  });

  const groupedByGroupName = groupedTasks.reduce<{ title: string; tasks: TaskModel[] }[]>((acc, { task, title }) => {
    const existingGroup = acc.find((group) => group.title === title);
    if (existingGroup) {
      existingGroup.tasks.push(task);
    } else {
      acc.push({ title, tasks: [task] });
    }
    return acc;
  }, []);
  return groupedByGroupName;
};

/** Group tasks by priority */
const groupTasksByPriority = (tasks: TaskModel[]): { title: string; tasks: TaskModel[] }[] => {
  const priorityGroups = [
    TASK_PRIORITY.URGENT,
    TASK_PRIORITY.HIGH,
    TASK_PRIORITY.MEDIUM,
    TASK_PRIORITY.LOW,
    TASK_PRIORITY.NO_PRIORITY
  ];
  return priorityGroups.reduce<{ title: string; tasks: TaskModel[] }[]>((acc, priority) => {
    const filteredTasks = tasks.filter((task) => {
      if (priority === TASK_PRIORITY.NO_PRIORITY) {
        return task.priority === TASK_PRIORITY.NO_PRIORITY || task.priority === null;
      }
      return task.priority === priority;
    });
    if (filteredTasks.length > 0) {
      acc.push({ title: TASK_PRIORITY_LABELS[priority], tasks: filteredTasks });
    }
    return acc;
  }, []);
};

/** Group tasks by due date */
const groupTasksByDate = (tasks: TaskModel[]) => {
  const todayStart = moment().startOf('day');
  const todayEnd = moment().endOf('day');

  const grouped = tasks.reduce(
    (acc, task) => {
      if (!task.dueDate) {
        acc.Other.push(task);
      } else {
        const taskDueDate = moment(task.dueDate);
        if (taskDueDate.isBefore(todayStart)) {
          acc.Delayed.push(task);
        } else if (taskDueDate.isBetween(todayStart, todayEnd, null, '[]')) {
          acc.Today.push(task);
        } else {
          acc.Upcoming.push(task);
        }
      }
      return acc;
    },
    { Delayed: [], Today: [], Upcoming: [], Other: [] } as Record<string, TaskModel[]>
  );

  return Object.entries(grouped)
    .filter(([, tasks]) => tasks.length > 0)
    .map(([title, tasks]) => ({ title, tasks }));
};

const groupByProject = (tasks: TaskModel[]) => {
  const groupedMap = tasks.reduce((acc, task) => {
    if (!acc.has(task.projectId)) {
      acc.set(task.projectId, {
        project_id: task.projectId,
        tasks: [],
        title: task.project?.name || ''
      });
    }
    acc.get(task.projectId)!.tasks.push(task);
    return acc;
  }, new Map<string, { project_id: string; tasks: TaskModel[]; title: string }>());
  return Array.from(groupedMap.values());
};

/** Group tasks based on the filter type */
export const groupMyTasksByFilter = (tasks: TaskModel[], filterType: number): MobxMyTaskModelInterface[] => {
  // Sort tasks by due date
  const sortedTasks = tasks.sort((a, b) => {
    // If either task has no due date, put it last
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;

    // Otherwise sort by due date
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });

  switch (filterType) {
    case MY_TASKS_GROUP_FILTER_TYPE.GROUP:
      return groupTasksByGroupName(sortedTasks);
    case MY_TASKS_GROUP_FILTER_TYPE.PROJECT:
      return groupByProject(sortedTasks);
    case MY_TASKS_GROUP_FILTER_TYPE.PRIORITY:
      return groupTasksByPriority(sortedTasks);
    case MY_TASKS_GROUP_FILTER_TYPE.NONE:
      return [{ title: '', tasks: sortedTasks }];
    default:
      return groupTasksByDate(sortedTasks);
  }
};

//---------------------------------------- calendar view services -----------------------------------------------------
export interface WeekDayTask {
  key: number;
  dayName: string;
  date: number;
  dateString: string;
  tasks: TaskModel[];
  completedTasks: TaskModel[];
}

export const getWeekTasks = (
  currentMonth: Date,
  weekTasks: TaskModel[],
  taskFilter?: {
    orderByStatus?: number;
    showRecentlyCompletedTask?: boolean;
  }
): WeekDayTask[] => {
  const startDate = startOfWeek(currentMonth, { weekStartsOn: 1 });
  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return weekDays.reduce<WeekDayTask[]>((days, dayName, i) => {
    const loopDate = moment(startDate).add(i, 'days').format('DD-MM-YYYY');
    const tasksForDay = weekTasks?.filter((x) => moment(x?.dueDate).format('DD-MM-YYYY') === loopDate);

    let thisDayTasks = tasksForDay?.filter((x) => !x?.status) || [];
    let completedTasks = tasksForDay?.filter((x) => x?.status) || [];

    if (taskFilter?.orderByStatus === SHOW_TASK_BY_ORDER.PRIORITY) {
      // PRIORITY
      const priorityGroups = [
        TASK_PRIORITY.URGENT,
        TASK_PRIORITY.HIGH,
        TASK_PRIORITY.MEDIUM,
        TASK_PRIORITY.LOW,
        TASK_PRIORITY.NO_PRIORITY
      ];
      const sortByPriority = (a: TaskModel, b: TaskModel) =>
        priorityGroups.indexOf(a?.priority || TASK_PRIORITY.NO_PRIORITY) -
        priorityGroups.indexOf(b?.priority || TASK_PRIORITY.NO_PRIORITY);

      thisDayTasks = thisDayTasks.toSorted(sortByPriority);
      completedTasks = completedTasks.toSorted(sortByPriority);
    } else if (taskFilter?.orderByStatus === 2) {
      // CREATED_DATE
      thisDayTasks = thisDayTasks.toSorted((a: TaskModel, b: TaskModel) => {
        const dateA = a?.dueDate ? new Date(a.dueDate) : new Date();
        const dateB = b?.dueDate ? new Date(b.dueDate) : new Date();
        return dateA.getTime() - dateB.getTime();
      });
      completedTasks = completedTasks.toSorted((a: TaskModel, b: TaskModel) => {
        const dateA = a?.dueDate || new Date();
        const dateB = b?.dueDate || new Date();
        return dateA.getTime() - dateB.getTime();
      });
    }

    if (taskFilter?.showRecentlyCompletedTask) {
      completedTasks = completedTasks.toSorted(
        (a, b) => new Date(b?.updatedTime || '').getTime() - new Date(a?.updatedTime || '').getTime()
      );
    }

    days.push({
      key: i + 1,
      dayName,
      date: Number(moment(startDate).add(i, 'days').format('DD')),
      dateString: moment(startDate).add(i, 'days').toDate().toISOString(),
      tasks: thisDayTasks,
      completedTasks
    });

    return days;
  }, []);
};
